import React from 'react'
import styles from '../SchedulingDayBox.module.scss'
import IconDragHandle from 'assets/images/icon-drag-handle.svg'
import closeIcon from 'assets/images/icon-close.svg'

interface SelectionRectProps {
  start: Date
  end: Date
  index?: number
  multiMode: boolean
  hasConflict: boolean
  eventTitle?: string
  conflictMessage?: string
  startY: number
  height: number
  timeDisplayStart: string
  timeDisplayEnd: string
  onRemove?: (() => void) | null
  onMouseDown?: ((e: React.MouseEvent) => void) | null
  onTouchStart?: ((e: React.TouchEvent) => void) | null
}

/**
 * SelectionRect Component
 *
 * Renders a single time selection rectangle with optional drag/resize handles
 * and remove button for multi-mode.
 */
const SelectionRect = ({
  start,
  end,
  // index,
  multiMode,
  hasConflict,
  eventTitle = '',
  conflictMessage = '',
  startY,
  height,
  timeDisplayStart,
  timeDisplayEnd,
  onRemove = null,
  onMouseDown = null,
  onTouchStart = null,
}: SelectionRectProps) => {
  const isSingleLine = (end.getTime() - start.getTime()) / (1000 * 60) < 45 // Less than 45 minutes

  const backgroundColor = hasConflict
    ? 'rgba(239, 68, 68, 0.5)' // Red for conflicts
    : 'rgba(16, 191, 121, 0.8)' // Green for available

  const borderColor = hasConflict ? '#EF4444' : '#10BF79'

  // Remove button click handler (separate from drag/resize interactions)
  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (multiMode && onRemove) {
      onRemove()
    }
  }

  // Prevent drag when clicking remove button
  const handleMouseDownOnRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const handleTouchStartOnRemove = (e: React.TouchEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }

  // Mouse/touch handlers for drag/resize (both single and multi-mode)
  const handleMouseDown = (e: React.MouseEvent) => {
    // Don't start drag if clicking on remove button
    if ((e.target as HTMLElement).closest(`.${styles.removeButton}`)) {
      return
    }
    if (onMouseDown) {
      onMouseDown(e)
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    // Don't start drag if touching remove button
    if ((e.target as HTMLElement).closest(`.${styles.removeButton}`)) {
      return
    }
    if (onTouchStart) {
      onTouchStart(e)
    }
  }

  return (
    <div
      className={styles.selectionOverlay}
      style={{
        position: 'absolute',
        top: `${startY}px`,
        left: 0,
        width: '100%',
        height: `${height}px`,
        backgroundColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '4px',
        zIndex: 200,
        pointerEvents: 'auto', // Always auto for interactions
        cursor: 'move',
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {/* Drag/resize handles - shown for both single and multi-mode */}
      {/* Top resize handle */}
      <div className={`${styles.resizeHandle} ${styles.top}`} />

      {/* Top drag handle icon */}
      <img
        src={IconDragHandle}
        alt="drag handle"
        className={`${styles.dragHandleIcon} ${styles.top}`}
      />

      {/* Content */}
      <div
        className={`${styles.contentWrapper} ${isSingleLine ? styles.singleLine : ''}`}
        style={{
          pointerEvents: 'none',
        }}
      >
        <div className={styles.timeText}>
          {timeDisplayStart} - {timeDisplayEnd}
          {eventTitle && `  ${eventTitle}`}
        </div>
        {conflictMessage && (
          <div className={`${styles.statusText} ${isSingleLine ? styles.singleLine : ''}`}>
            {isSingleLine ? ' - ' : ''}
            {conflictMessage}
          </div>
        )}
      </div>

      {/* Multi-mode: Remove button (X) with separate wrapper for better event handling */}
      {multiMode && onRemove && (
        <div
          className={styles.removeButton}
          onClick={handleRemoveClick}
          onMouseDown={handleMouseDownOnRemove}
          onTouchStart={handleTouchStartOnRemove}
        >
          <img src={closeIcon} alt="Remove" />
        </div>
      )}

      {/* Bottom handles - shown for both single and multi-mode */}
      {/* Bottom resize handle */}
      <div className={`${styles.resizeHandle} ${styles.bottom}`} />

      {/* Bottom drag handle icon */}
      <img
        src={IconDragHandle}
        alt="drag handle"
        className={`${styles.dragHandleIcon} ${styles.bottom}`}
      />
    </div>
  )
}

export default SelectionRect
